import { CaptainAdvance, CaptainDTO } from '../types';

export const CAPTAIN_CREATURE_ID = 'captain';
export const CAPTAIN_HIRE_COST = 100;
export const CAPTAIN_MAX_LEVEL = 10;
export const CAPTAIN_STARTING_TRICK_COUNT = 2;
export const CAPTAIN_MAX_HEALTH_ADVANCES = 2;
export const CAPTAIN_MAX_WILL_ADVANCES = 1;

export interface CaptainStatIncrease {
  id: string;
  label: string;
  stat: 'move' | 'fight' | 'shoot' | 'will' | 'health';
  amount: number;
}

export const CAPTAIN_STAT_INCREASES: CaptainStatIncrease[] = [
  { id: 'move', label: '+1 Move', stat: 'move', amount: 1 },
  { id: 'fight', label: '+1 Fight', stat: 'fight', amount: 1 },
  { id: 'shoot', label: '+1 Shoot', stat: 'shoot', amount: 1 },
  { id: 'will', label: '+1 Will', stat: 'will', amount: 1 },
  { id: 'health', label: '+2 Health', stat: 'health', amount: 2 },
];

// Captain Equipment Table (Sellsword) — captain-specific prices
export const CAPTAIN_WEAPON_PRICES: Record<string, number> = {
  hand_weapon: 0,
  dagger: 5,
  bow: 5,
  crossbow: 5,
  two_handed_weapon: 5,
  staff: 5,
};

export const CAPTAIN_ARMOUR_PRICES: Record<string, number> = {
  light_armour: 0,
  heavy_armour: 5,
  shield: 5,
};

export function getCaptainEquipmentCost(weaponIds: string[], armourIds: string[]): number {
  const weaponCost = weaponIds.reduce((sum, id) => sum + (CAPTAIN_WEAPON_PRICES[id] ?? 0), 0);
  const armourCost = armourIds.reduce((sum, id) => sum + (CAPTAIN_ARMOUR_PRICES[id] ?? 0), 0);
  return weaponCost + armourCost;
}

/**
 * Checks the Sellsword equipment combination rules. Returns warning messages
 * (non-blocking — house rules may allow other combinations).
 */
export function getCaptainEquipmentWarnings(weaponIds: string[], armourIds: string[]): string[] {
  const warnings: string[] = [];
  const has = (id: string) => weaponIds.includes(id);
  const hasArmour = (id: string) => armourIds.includes(id);

  const bigWeapon = has('staff') || has('two_handed_weapon');
  if (bigWeapon) {
    if (hasArmour('shield')) {
      warnings.push('A captain armed with a staff or two-handed weapon may not carry a shield.');
    }
    const otherWeapons = weaponIds.filter((id) => !['staff', 'two_handed_weapon', 'bow', 'crossbow'].includes(id));
    if (otherWeapons.length > 0) {
      warnings.push('A captain armed with a staff or two-handed weapon may not carry other weapons except a bow or crossbow.');
    }
    if (has('staff') && has('two_handed_weapon')) {
      warnings.push('A captain may not carry both a staff and a two-handed weapon.');
    }
  }
  if (has('dagger') && hasArmour('shield')) {
    warnings.push('A captain can have either a dagger or a shield, but not both.');
  }
  if (hasArmour('light_armour') && hasArmour('heavy_armour')) {
    warnings.push('A captain may wear either leather or mail armour, but not both.');
  }
  return warnings;
}

/** A captain's level equals the number of advances he has taken (max 10). */
export function getCaptainLevel(advances: CaptainAdvance[]): number {
  return Math.min(advances.length, CAPTAIN_MAX_LEVEL);
}

/** Level-ups earned (100 XP each) but not yet spent on an advance. */
export function getUnusedCaptainLevelUps(captain: Pick<CaptainDTO, 'experience' | 'advances'>): number {
  const earned = Math.min(Math.floor(captain.experience / 100), CAPTAIN_MAX_LEVEL);
  return Math.max(0, earned - captain.advances.length);
}

/** Share of the treasure: (level + 10)% of all gold acquired, rounded down. */
export function getCaptainSharePercent(level: number): number {
  return level + 10;
}

export function getCaptainGoldShare(goldAcquired: number, level: number): number {
  return Math.floor((goldAcquired * getCaptainSharePercent(level)) / 100);
}

export function countCaptainAdvances(advances: CaptainAdvance[], type: CaptainAdvance['advance']): number {
  return advances.filter((a) => a.advance === type).length;
}
